/** Nutzereinstellungen (Route /settings/:tab, Vollbild, Esc schließt zurück zur App). */
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '../../components/Modal';
import { SettingsHeader, SettingsLayout, type SettingsSection } from '../../components/SettingsLayout';
import { isDesktopApp } from '../../lib/desktopApp';
import { paths } from '../../lib/routes';
import { logout } from '../auth/session';
import { extraMenuItems } from '../extras';
import { AccessibilityTab } from './AccessibilityTab';
import { AccountTab } from './AccountTab';
import { AppearanceTab } from './AppearanceTab';
import { DesktopAppTab } from './DesktopAppTab';
import { NotificationsTab } from './NotificationsTab';
import { ProfileTab } from './ProfileTab';
import { ServerProfileTab } from './ServerProfileTab';
import './settings.css';

const BUILTIN: (SettingsSection & { render?: () => React.ReactNode; hidden?: () => boolean })[] = [
  { id: 'konto', label: 'Mein Konto', group: 'Benutzereinstellungen', render: () => <AccountTab /> },
  { id: 'profil', label: 'Profile', group: 'Benutzereinstellungen', render: () => <ProfileTab /> },
  { id: 'server-profil', label: 'Server-Profile', group: 'Benutzereinstellungen', render: () => <ServerProfileTab /> },
  { id: 'darstellung', label: 'Darstellung', group: 'App-Einstellungen', render: () => <AppearanceTab /> },
  { id: 'barrierefreiheit', label: 'Barrierefreiheit', group: 'App-Einstellungen', render: () => <AccessibilityTab /> },
  { id: 'benachrichtigungen', label: 'Benachrichtigungen', group: 'App-Einstellungen', render: () => <NotificationsTab /> },
  // Download-Link nur im Browser – in der Desktop-App selbst sinnlos (User-Agent-Merkmal, lib/desktopApp.ts).
  { id: 'desktop-app', label: 'Desktop-App', group: 'App-Einstellungen', render: () => <DesktopAppTab />, hidden: () => isDesktopApp() },
];

export function UserSettings() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const extras = extraMenuItems.filter((i) => i.slot === 'userSettings');
  const builtins = BUILTIN.filter((t) => !t.hidden?.());
  const sections: SettingsSection[] = [
    ...builtins.map(({ render: _r, hidden: _h, ...s }) => s),
    ...extras.map((e) => ({ id: `extra-${e.id}`, label: e.label, group: 'Erweiterungen' })),
    { id: 'logout', label: 'Abmelden', danger: true, testId: 'settings-logout' },
  ];
  const active = sections.some((s) => s.id === tab && s.id !== 'logout') ? tab! : 'konto';

  const close = useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) void navigate(-1);
    else void navigate(paths.home());
  }, [navigate]);

  const select = (id: string) => {
    if (id === 'logout') {
      setConfirmLogout(true);
      return;
    }
    const extra = extras.find((e) => `extra-${e.id}` === id);
    if (extra?.to && !extra.component) {
      void navigate(extra.to);
      return;
    }
    void navigate(paths.settings(id), { replace: true });
  };

  let content: React.ReactNode = null;
  const builtin = builtins.find((t) => t.id === active);
  if (builtin?.render) content = builtin.render();
  else {
    const extra = extras.find((e) => `extra-${e.id}` === active);
    if (extra?.component) content = <extra.component onClose={close} />;
  }

  return (
    <>
      <SettingsLayout
        title="Einstellungen"
        sections={sections}
        active={active}
        onSelect={select}
        onClose={close}
        mobileView={tab ? 'content' : 'nav'}
        onMobileBack={() => void navigate('/settings', { replace: true })}
        testId="settings"
      >
        <div data-testid="user-settings" data-tab={active}>
          <SettingsHeader title={sections.find((s) => s.id === active)?.label ?? 'Einstellungen'} />
          {content}
        </div>
      </SettingsLayout>
      {confirmLogout && (
        <ConfirmDialog
          title="Abmelden?"
          message="Möchtest du dich auf diesem Gerät abmelden?"
          confirmLabel="Abmelden"
          danger
          onClose={() => setConfirmLogout(false)}
          onConfirm={() => void logout().then(() => navigate(paths.login()))}
        />
      )}
    </>
  );
}
